import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Plus, UserPlus } from 'lucide-react';

import { CreateOrganization } from '../components/CreateOrganization';
import { Dialog } from '../components/Dialog';
import { Callout, ErrorCallout, InlineConfirm, PageHead, SkeletonRows, Spinner } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { ago, fullTime, inFuture, shortDate } from '../lib/format';
import { useAction, useDocumentTitle, useNow, useResource } from '../lib/hooks';
import { useSession } from '../lib/session';
import type { Invite, Member, Role } from '../lib/types';

type Notice = { tone: 'ok' | 'info'; text: string };
type InviteAnswer = { member?: Member; invite?: Invite; emailed: boolean };

export function MembersPage() {
  useDocumentTitle('Members');
  const { me } = useSession();
  if (me?.team.kind === 'personal') return <PersonalMembers />;
  return <OrganizationMembers />;
}

/** A personal account is one person: point at organizations instead of an invite form that would refuse. */
function PersonalMembers() {
  const [creating, setCreating] = useState(false);
  return (
    <>
      <PageHead title="Members" />
      <section className="band">
        <div className="orgcta">
          <span className="orgcta__icon" aria-hidden="true">
            <Building2 />
          </span>
          <h2 className="orgcta__title">Organizations support member invitations</h2>
          <p>Personal accounts can’t invite members. Create an organization to work with others.</p>
          <button type="button" className="btn btn--primary" onClick={() => setCreating(true)}>
            <Plus aria-hidden="true" />
            Create organization
          </button>
        </div>
      </section>
      <CreateOrganization open={creating} onClose={() => setCreating(false)} />
    </>
  );
}

function OrganizationMembers() {
  const { me, refreshMe } = useSession();
  const navigate = useNavigate();
  const now = useNow(30_000);
  const res = useResource<{ members: Member[]; invites: Invite[] }>('/v1/team/members');
  const [inviting, setInviting] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [pending, setPending] = useState<string | null>(null);

  const send = useAction(async (email: string) => api<InviteAnswer>('/v1/team/members', { method: 'POST', body: { email } }));
  const setRole = useAction(async (userId: string, role: Role) => api<Member>(`/v1/team/members/${encodeURIComponent(userId)}`, { method: 'PATCH', body: { role } }));
  const remove = useAction(async (userId: string) => {
    await api(`/v1/team/members/${encodeURIComponent(userId)}`, { method: 'DELETE' });
    return true;
  });
  /* 404 means it's no longer pending: withdrawn elsewhere, or they signed up and joined. Either way, refetch. */
  const withdraw = useAction(async (email: string): Promise<'withdrawn' | 'gone'> => {
    try {
      await api(`/v1/team/invites/${encodeURIComponent(email)}`, { method: 'DELETE' });
      return 'withdrawn';
    } catch (err) {
      if (err instanceof ApiError && err.code === 'not-found') return 'gone';
      throw err;
    }
  });
  const failure = setRole.error ?? remove.error ?? withdraw.error ?? (inviting ? undefined : send.error);

  function clear() {
    setNotice(null);
    setRole.setError(undefined);
    remove.setError(undefined);
    withdraw.setError(undefined);
    send.setError(undefined);
  }

  /** The words for a sent invite, which only say "emailed" when a mail went out. */
  function sent(answer: InviteAnswer, renewal: boolean): Notice {
    if (answer.member) return { tone: 'ok', text: `${answer.member.email} already had an account and is now a member.` };
    const email = answer.invite?.email ?? '';
    const until = answer.invite ? shortDate(answer.invite.expiresAt) : '';
    if (!answer.emailed) return { tone: 'info', text: `${renewal ? 'Renewed' : 'Invited'} ${email} until ${until}. They were emailed less than 10 minutes ago, so no new email went out.` };
    return { tone: 'ok', text: `${renewal ? 'Sent a new invite to' : 'Invited'} ${email}. It lasts until ${until}.` };
  }

  async function invite(email: string): Promise<boolean> {
    clear();
    const answer = await send.run(email);
    if (!answer) return false;
    setNotice(sent(answer, false));
    await res.reload();
    return true;
  }

  async function resend(email: string) {
    clear();
    setPending(`resend:${email}`);
    const answer = await send.run(email);
    setPending(null);
    if (!answer) return;
    setNotice(sent(answer, true));
    await res.reload();
  }

  async function changeRole(member: Member, role: Role) {
    clear();
    setPending(`role:${member.userId}`);
    const updated = await setRole.run(member.userId, role);
    setPending(null);
    if (!updated) return;
    if (member.userId === me?.user.id) await refreshMe();
    await res.reload();
  }

  /* Leaving moves the session to another of your accounts; with none left, the plane signs you out. */
  async function removeMember(member: Member, self: boolean) {
    clear();
    setPending(`remove:${member.userId}`);
    const ok = await remove.run(member.userId);
    setPending(null);
    if (!ok) return;
    if (self) {
      const next = await refreshMe();
      if (next) navigate('/', { replace: true });
      return;
    }
    await res.reload();
  }

  async function withdrawInvite(email: string) {
    clear();
    setPending(`withdraw:${email}`);
    const outcome = await withdraw.run(email);
    setPending(null);
    if (!outcome) return;
    setNotice(outcome === 'withdrawn' ? { tone: 'ok', text: `Withdrew the invite to ${email}.` } : { tone: 'info', text: `${email} had no invite left: it was withdrawn, or they joined.` });
    await res.reload();
  }

  const members = res.data?.members ?? [];
  const invites = res.data?.invites ?? [];
  const isOwner = me?.role === 'owner';
  const owners = members.filter((m) => m.role === 'owner').length;

  return (
    <>
      <PageHead
        title="Members"
        lede={`Everyone in ${me?.team.name ?? 'this organization'} shares its keys, sandboxes and bill.`}
        actions={
          isOwner ? (
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => {
                send.setError(undefined);
                setInviting(true);
              }}
            >
              <UserPlus aria-hidden="true" />
              Invite member
            </button>
          ) : null
        }
      />
      {notice || failure ? (
        <div className="stack mb-16">
          {failure ? <ErrorCallout error={failure} title={failure.code === 'last-owner' ? 'An organization needs at least one owner' : undefined} /> : null}
          {notice ? <Callout tone={notice.tone} role="status" title={notice.text} /> : null}
        </div>
      ) : null}
      <section className="band band--flush">
        <div className="band__head">
          <h2 className="h2">Members</h2>
          {members.length ? <span className="muted small">{members.length}</span> : null}
        </div>
        {res.error ? (
          <div className="px">
            <ErrorCallout error={res.error} onRetry={() => void res.reload()} />
          </div>
        ) : null}
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">Email</th>
                <th scope="col">Role</th>
                <th scope="col">Joined</th>
                <th scope="col" className="r">
                  <span className="visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {res.loading && !res.data ? (
                <SkeletonRows cols={4} rows={2} />
              ) : (
                members.map((m) => {
                  const self = m.userId === me?.user.id;
                  /* The last owner can't step down; the plane would refuse it anyway. */
                  const locked = m.role === 'owner' && owners <= 1;
                  return (
                    <tr key={m.userId} className="m-stack">
                      <td className="m-title" style={{ maxWidth: 360 }}>
                        <span className="cell-trunc" style={{ fontWeight: 600 }}>
                          {m.name || m.email}
                          {self ? <span className="tag tag--accent" style={{ marginLeft: 8, verticalAlign: 1 }}>You</span> : null}
                        </span>
                        {m.name ? <span className="cell-sub cell-trunc">{m.email}</span> : null}
                      </td>
                      {/* On a phone: name and action on line one; role and joined share line two. */}
                      <td className={isOwner && !locked ? 'm-meta m-inline' : 'm-meta'} data-label={isOwner && !locked ? undefined : 'Role'}>
                        {isOwner && !locked ? (
                          <span className="row" style={{ gap: 6 }}>
                            <select
                              className="select select--sm"
                              aria-label={`Role for ${m.name || m.email}`}
                              value={m.role}
                              disabled={pending !== null}
                              onChange={(e) => void changeRole(m, e.target.value as Role)}
                            >
                              <option value="owner">Owner</option>
                              <option value="member">Member</option>
                            </select>
                            {pending === `role:${m.userId}` ? <Spinner size={13} /> : null}
                          </span>
                        ) : (
                          <span className="tag">{m.role === 'owner' ? 'Owner' : 'Member'}</span>
                        )}
                      </td>
                      <td className="m-meta nowrap" data-label="Joined" title={fullTime(m.addedAt)}>
                        {ago(m.addedAt, now)}
                      </td>
                      <td className="r m-end">
                        {(isOwner || self) && !(self && locked) ? (
                          <InlineConfirm
                            trigger={self ? 'Leave' : 'Remove'}
                            question={self ? 'Leave this organization? Your keys on it stop working.' : `Remove ${m.name || m.email}? Their keys stop working.`}
                            confirmLabel={self ? 'Leave' : 'Remove'}
                            busy={pending === `remove:${m.userId}`}
                            onConfirm={() => removeMember(m, self)}
                          />
                        ) : null}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>
      <section className="band band--flush">
        <div className="band__head">
          <h2 className="h2">Invitations</h2>
          {invites.length ? <span className="muted small">{invites.length}</span> : null}
        </div>
        {invites.length === 0 ? (
          <p className="px muted" style={{ margin: '0 0 20px' }}>
            {isOwner ? 'No pending invitations. Invite someone with their email; they join when they sign up.' : 'No pending invitations.'}
          </p>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">Email</th>
                  <th scope="col">Invited by</th>
                  <th scope="col">Expires</th>
                  <th scope="col">Status</th>
                  {isOwner ? (
                    <th scope="col" className="r">
                      <span className="visually-hidden">Actions</span>
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {invites.map((i) => {
                  const expired = i.status === 'expired';
                  return (
                    <tr key={i.email} className="m-stack">
                      <td className="m-title">
                        <span className="cell-trunc">{i.email}</span>
                      </td>
                      <td className="m-meta" data-label="Invited by">
                        <span className="cell-trunc">{i.invitedBy ?? <span className="muted">Former member</span>}</span>
                      </td>
                      <td className="m-meta nowrap" data-label="Expires" title={fullTime(i.expiresAt)}>
                        {expired ? ago(i.expiresAt, now) : inFuture(i.expiresAt, now)}
                      </td>
                      <td className="m-meta m-inline">
                        <span className={`pill ${expired ? 'pill--muted' : ''}`}>
                          <span className={`lamp ${expired ? 'lamp--off' : 'lamp--hollow'}`} aria-hidden="true" />
                          {expired ? 'Expired' : 'Pending'}
                        </span>
                      </td>
                      {isOwner ? (
                        <td className="r m-end">
                          <span className="row" style={{ gap: 4, justifyContent: 'flex-end' }}>
                            <button type="button" className="btn btn--sm btn--quiet" disabled={pending !== null} aria-busy={pending === `resend:${i.email}`} onClick={() => void resend(i.email)}>
                              {pending === `resend:${i.email}` ? <Spinner size={13} /> : null}
                              {expired ? 'Renew' : 'Resend'}
                            </button>
                            <InlineConfirm
                              trigger="Withdraw"
                              question="Withdraw this invite?"
                              confirmLabel="Withdraw"
                              dismissLabel="Keep it"
                              busy={pending === `withdraw:${i.email}`}
                              onConfirm={() => withdrawInvite(i.email)}
                            />
                          </span>
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <InviteDialog open={inviting} onClose={() => setInviting(false)} busy={send.busy} error={inviting ? send.error : undefined} onInvite={invite} />
    </>
  );
}

function InviteDialog({ open, onClose, busy, error, onInvite }: { open: boolean; onClose: () => void; busy: boolean; error: ApiError | undefined; onInvite: (email: string) => Promise<boolean> }) {
  const [email, setEmail] = useState('');
  function close() {
    if (busy) return;
    setEmail('');
    onClose();
  }
  async function submit(e: FormEvent) {
    e.preventDefault();
    const address = email.trim();
    if (!address) return;
    if (await onInvite(address)) {
      setEmail('');
      onClose();
    }
  }
  return (
    <Dialog open={open} onClose={close} title="Invite member" description="They join as a member when they sign up with this address. The invite lasts 7 days.">
      <form className="stack" onSubmit={submit}>
        <div className="field">
          <label htmlFor="invite-email">Email</label>
          <input id="invite-email" className="input" type="email" inputMode="email" autoComplete="off" autoFocus placeholder="teammate@company.com" value={email} onChange={(e) => setEmail(e.target.value)} />
        </div>
        {error ? <ErrorCallout error={error} /> : null}
        <div className="dialog__actions">
          <button type="button" className="btn" onClick={close} disabled={busy}>
            Cancel
          </button>
          <button type="submit" className="btn btn--primary" disabled={!email.trim() || busy} aria-busy={busy}>
            {busy ? <Spinner /> : null}
            Invite
          </button>
        </div>
      </form>
    </Dialog>
  );
}
